import { useCallback } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'

import {
  createRundownRunner,
  endRunnerTimesnap,
  pauseRunnerTimesnap,
  startRunnerTimesnap,
  startRunnerTimesnapOnPreviousCue,
  unsetRundownRunner,
  updateRundownCue,
} from '../../../firestore'

import { cuesAtom, firstCueIdAtom, rundownAtom } from '@/store/rundown.store'
import { runnerAtom } from '@/store/runner.store'

export const isRunnerLoadingAtom = atom<boolean>(false)

export const nextCueIdAtom = atom((get) => get(runnerAtom)?.nextCueId)

export const isFirstCueAtom = atom<boolean>(
  (get) => get(firstCueIdAtom) === get(runnerAtom)?.timesnap.cueId,
)

export function useTransportActions() {
  const rundown = useAtomValue(rundownAtom)
  const runner = useAtomValue(runnerAtom)
  const cues = useAtomValue(cuesAtom)

  const firstCueId = useAtomValue(firstCueIdAtom)

  const setLoading = useSetAtom(isRunnerLoadingAtom)

  async function onPlay() {
    if (!rundown) return
    setLoading(true)
    await startRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }

  async function onPause() {
    if (!rundown) return
    setLoading(true)
    await pauseRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }

  async function onPrev() {
    if (!rundown) return
    setLoading(true)
    await startRunnerTimesnapOnPreviousCue(
      rundown.id,
      rundown.runnerId,
    ).finally(() => setLoading(false))
  }

  async function onNext() {
    if (!rundown || !runner) return
    setLoading(true)
    await startRunnerTimesnap(
      rundown.id,
      rundown.runnerId,
      runner.nextCueId,
    ).finally(() => setLoading(false))
  }

  async function onCreateRunner() {
    if (!rundown) return
    setLoading(true)
    await createRundownRunner(rundown.id).finally(() => setLoading(false))
  }

  async function onUnsetRunner() {
    if (!rundown) return
    setLoading(true)
    await unsetRundownRunner(rundown.id).finally(() => setLoading(false))
  }

  const onEndRunner = useCallback(async () => {
    if (!rundown) return
    setLoading(true)
    await endRunnerTimesnap(rundown.id, rundown.runnerId).finally(() =>
      setLoading(false),
    )
  }, [rundown, setLoading])

  async function onTimeOperation(amount) {
    if (!rundown || !runner) return
    setLoading(true)
    const duration = Math.max(cues[runner.timesnap.cueId].duration + amount, 0)
    const isFirstCue = runner.timesnap.cueId === firstCueId
    await updateRundownCue(
      rundown.id,
      runner.timesnap.cueId,
      { duration },
      isFirstCue,
    ).finally(() => setLoading(false))
  }

  return {
    onPlay,
    onPause,
    onPrev,
    onNext,
    onCreateRunner,
    onUnsetRunner,
    onEndRunner,
    onTimeOperation,
  }
}
